import { useState } from 'react';
import { Inbox } from 'lucide-react';
import { Pagination } from '@/components/shared/Pagination';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { formatDataHora } from '@/lib/utils/formatters';
import { useListaEventos } from '@/services/Documentos';

const POR_PAGINA = 10;

interface Props {
  chaveAcesso: string;
  codigoModelo: string;
}

/**
 * A linha do tempo da nota (SPEC-018, §1, decisão 5): os eventos da SEFAZ e, na NF-e liberada, a liberação no
 * Recebimento, com a data, na ordem que a API manda. A liberação se destaca porque não é evento da SEFAZ.
 */
export function TabEventos({ chaveAcesso, codigoModelo }: Props) {
  const [pagina, setPagina] = useState(1);
  const { data, isLoading } = useListaEventos(
    { TipoDocumento: codigoModelo, ChaveAcessoDOC: chaveAcesso },
    !!chaveAcesso
  );

  const todos = data?.data ?? [];
  const totalPaginas = Math.max(1, Math.ceil(todos.length / POR_PAGINA));
  const inicio = (pagina - 1) * POR_PAGINA;
  const pagAtual = todos.slice(inicio, inicio + POR_PAGINA);

  return (
    <div className="space-y-3">
      <div className="overflow-auto rounded-md border">
        <Table>
          <TableHeader className="bg-[#f7f7f7]">
            <TableRow>
              <TableHead>Descrição</TableHead>
              <TableHead>Código</TableHead>
              <TableHead>Mensagem</TableHead>
              <TableHead>Autor</TableHead>
              <TableHead>Origem</TableHead>
              <TableHead>Protocolo SEFAZ</TableHead>
              <TableHead>Data/Hora Envio</TableHead>
              <TableHead>Data/Hora Recibo</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              Array.from({ length: POR_PAGINA }).map((_, index) => (
                <TableRow key={index}>
                  {Array.from({ length: 8 }).map((__, cellIndex) => (
                    <TableCell key={cellIndex}>
                      <div className="h-3.5 w-3/4 rounded bg-muted animate-pulse" />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : todos.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} className="py-10 text-center text-sm text-muted-foreground">
                  Nenhum evento encontrado.
                </TableCell>
              </TableRow>
            ) : (
              pagAtual.map((evento, idx) => (
                <TableRow
                  key={`${evento.Chave}-${(pagina - 1) * POR_PAGINA + idx}`}
                  className={
                    evento.LiberacaoNoRecebimento
                      ? 'bg-emerald-50 hover:bg-emerald-100/70 dark:bg-emerald-950/20'
                      : undefined
                  }
                  data-testid={
                    evento.LiberacaoNoRecebimento ? 'liberacao-no-recebimento' : undefined
                  }
                >
                  <TableCell className="min-w-[220px] text-sm font-medium">
                    {evento.LiberacaoNoRecebimento ? (
                      <span className="flex items-center gap-2">
                        <Inbox className="h-4 w-4 shrink-0 text-emerald-700" aria-hidden="true" />
                        {evento.Descricao}
                      </span>
                    ) : (
                      evento.Descricao || '—'
                    )}
                  </TableCell>
                  <TableCell className="whitespace-nowrap text-xs font-mono">
                    {evento.CodigoResposta || '—'}
                  </TableCell>
                  <TableCell className="min-w-[280px] text-sm">
                    {evento.MensagemResposta || '—'}
                  </TableCell>
                  <TableCell className="whitespace-nowrap text-sm">
                    {evento.DocumentoAutor || '—'}
                  </TableCell>
                  <TableCell className="whitespace-nowrap text-sm">
                    {evento.Origem || '—'}
                  </TableCell>
                  <TableCell className="whitespace-nowrap text-xs font-mono">
                    {evento.ProtocoloSefaz || '—'}
                  </TableCell>
                  <TableCell className="whitespace-nowrap text-sm">
                    {formatDataHora(evento.DataHoraEnvio)}
                  </TableCell>
                  <TableCell className="whitespace-nowrap text-sm">
                    {formatDataHora(evento.DataHoraRecibo)}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {todos.length > 0 && (
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <span className="shrink-0">{todos.length} evento(s)</span>
          <div className="flex-1">
            <Pagination currentPage={pagina} totalPages={totalPaginas} onPageChange={setPagina} />
          </div>
        </div>
      )}
    </div>
  );
}
